/**
 * R13 / BR-DSH-10-20 — the user-selectable columns of a report's detail
 * table: which columns, in what order, starting from the delivered default.
 * Pure logic; the panel component calls these and the page persists the
 * result inside a saved configuration (BR-DSH-10-22).
 *
 * BR-DSH-10-20's fence — "a column the person's role may not read must not
 * be offered" — is upstream: the API never serves a field the caller may
 * not read, and the pickable set is derived from the response shape, so an
 * unreadable column cannot even be described here.
 */

export interface ColumnDef {
  id: string;
  labelKey: string;
}

/** Toggle: ticking adds at the END (the user then reorders); unticking removes. */
export function toggleColumn(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((c) => c !== id) : [...selected, id];
}

/** Reorder one column a step towards the start or the end. */
export function moveColumn(selected: readonly string[], id: string, direction: -1 | 1): string[] {
  const at = selected.indexOf(id);
  const to = at + direction;
  if (at < 0 || to < 0 || to >= selected.length) return [...selected];
  const next = [...selected];
  next.splice(at, 1);
  next.splice(to, 0, id);
  return next;
}

/**
 * Resolve a saved (or default) selection against the report's available set:
 * unknown ids are dropped (a column removed from the product), and an empty
 * result falls back to the delivered default — a report with zero columns is
 * not a report.
 */
export function resolveColumns(
  available: readonly ColumnDef[],
  selected: readonly string[],
  defaults: readonly string[],
): string[] {
  const known = new Set(available.map((c) => c.id));
  const kept = selected.filter((id) => known.has(id));
  return kept.length > 0 ? kept : defaults.filter((id) => known.has(id));
}

/**
 * Is the configuration a reader applied still IN FORCE?
 *
 * DSH-10 Band 6 (BR-DSH-10-16): the printed heading block "must name the
 * configuration if one is in use". "In use" is decided by COMPARISON, not by
 * memory of a click: the name is claimed only while the page's current
 * columns, captured filters and period still equal what the configuration
 * set. A reader who applies "Month-end pack" and then moves a date or drops
 * a column is no longer looking at Month-end pack, and a heading that still
 * named it would attribute figures to a recipe that did not produce them —
 * and the comparison also means that undoing the change honestly restores
 * the name, with no clearing logic for anybody to forget in a setter.
 *
 * Field-by-field rather than JSON.stringify because a saved body round-trips
 * through the server and key ORDER is not part of what "the same choices"
 * means.
 */
interface ConfigChoices {
  columns: readonly string[];
  filters: Record<string, string>;
  period: { preset: string; from?: string | undefined; to?: string | undefined };
}

export function configInForce(
  applied: ConfigChoices,
  current: ConfigChoices,
  /**
   * Whether the screen HAS a window for a configuration to have chosen.
   *
   * `false` on DSH-07 only, under delegated ruling A14: that screen is
   * as-of-now and its date boxes are removed (BR-DSH-10-02's exemption, see
   * `inventory-report-page.tsx`). A configuration saved there before the
   * ruling still carries the dead `preset`/`from`/`to` it was stored with, and
   * comparing them would mean no pre-A14 configuration could EVER read as in
   * force — the reader applies it, the screen adopts every choice it actually
   * makes, and the heading silently declines to name it. The period is not one
   * of its choices on a screen that has no period.
   */
  options: { readonly hasWindow?: boolean } = {},
): boolean {
  if (applied.columns.length !== current.columns.length) return false;
  if (applied.columns.some((id, at) => current.columns[at] !== id)) return false;
  // Only the fields the configuration SPECIFIED are its claims. A saved body
  // carrying `preset` alone set no dates, so the window the preset computed
  // cannot un-name it — while typing a date flips the preset to `custom`,
  // which is a mismatch this comparison does see.
  for (const key of Object.keys(applied.filters)) {
    if (applied.filters[key] !== current.filters[key]) return false;
  }
  if (options.hasWindow === false) return true;
  if (applied.period.preset !== current.period.preset) return false;
  if (applied.period.from !== undefined && applied.period.from !== current.period.from) return false;
  if (applied.period.to !== undefined && applied.period.to !== current.period.to) return false;
  return true;
}
